/**
 * EPIC 54 §2.1 — the one EMPLOYER company this account belongs to.
 *
 * One account, one company. There is nothing to choose, so there is no
 * selector and no `ettevote` parameter: a URL cannot pick a company, only
 * name one to be checked against this account's own.
 *
 * Screens each used to take the first entry in `permissions`, which is the
 * LEGACY link list — so two screens could disagree about whose data was on
 * display, and an account that predates the rule silently showed whichever
 * company happened to sort first. This is the single authoritative answer,
 * and it draws on BOTH sources: an account with a new-model membership at one
 * company and a legacy link to another is in conflict too, and a query of
 * membership documents alone would not see it.
 *
 * On a conflict there is NO active company. Choosing one silently is the harm
 * — it shows one company's data under another's name — so the portal says so
 * and the conflict goes to a person to resolve.
 *
 * WHAT COUNTS AS A COMPANY is the other half, and it was missing. A
 * `type: 'individual'` client is one person's own B2C record: no team, no
 * "Inimesed" view, no billing rights to hand out. `oneCompanyRule` on the
 * server already says so and lets a B2B member create one — and then this
 * context, which merged every membership and every `permissions` id without
 * a type, counted two ids and declared a conflict. A site manager who
 * ordered a handyman for their own home lost their employer from every list
 * in the portal. The server permitted a state the portal refused to read.
 *
 * The fix is not to pick the first id. It is to keep the two contexts apart:
 * the employer company is what every B2B screen is scoped to, and the
 * personal client is what a personal order belongs to. The TYPE comes from
 * the server, because a client cannot read a `clients` document it has not
 * been let into.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';

import { useAuth } from '../auth/AuthContext';
import { isStaff } from '../auth/isStaff';
import { getFirebase } from '../services/firebase';
import {
  listMyClientInvitations,
  type ClientKind,
  type MyMembership,
} from '../services/clientInvitations';

interface ActiveCompany {
  /** Null while loading, when there is none, AND when there is a conflict. */
  clientId: string | null;
  companyName: string | null;
  loading: boolean;
  /** True when this account is linked to more than one EMPLOYER company. */
  conflict: boolean;
  /** The companies in conflict, so the person can be told which to resolve. */
  conflictingClientIds: string[];
  /**
   * This person's own B2C client records — a personal order's home, and
   * never a company. Normally none or one; an array because a duplicate is
   * a data question, not a reason to guess.
   */
  personalClientIds: string[];
  /** Reload after accepting an invitation, without a page reload. */
  refresh: () => Promise<void>;
  /**
   * RM24 staff in the client portal act FOR a client they choose (Marti
   * 2026-10-22: admin enters an order for a caller who is not a member).
   * Staff have no membership, so the company comes from this choice.
   */
  actingAsStaff: boolean;
  setActingClient: (clientId: string | null) => void;
}

const Ctx = createContext<ActiveCompany>({
  clientId: null, companyName: null, loading: true, conflict: false,
  conflictingClientIds: [], personalClientIds: [], refresh: async () => {},
  actingAsStaff: false, setActingClient: () => {},
});

/** Per browser tab: a staff member's chosen client survives a reload, not a new tab. */
const ACTING_KEY = 'rm24.actingClientId';
function readActing(): string | null {
  try {
    return sessionStorage.getItem(ACTING_KEY);
  } catch {
    return null;
  }
}
/**
 * Admin screens open the client portal for a given client: the "Uus tellimus"
 * button on a client's card sets it here, before the portal mounts.
 */
export function actForClient(id: string): void {
  writeActing(id);
}

function writeActing(id: string | null): void {
  try {
    if (id) sessionStorage.setItem(ACTING_KEY, id);
    else sessionStorage.removeItem(ACTING_KEY);
  } catch {
    /* private mode — the choice simply does not persist */
  }
}

/**
 * The type of a client the callable could not tell us about.
 *
 * Only reached when `listMyClientInvitations` itself failed. A read that is
 * refused or missing is treated as a COMPANY: that is the conservative
 * answer, because mistaking a company for a personal record would hide a
 * real two-company conflict, while the reverse merely reports one.
 */
async function kindOfClient(clientId: string): Promise<ClientKind> {
  try {
    const { firestore } = getFirebase();
    const { doc, getDoc } = await import('firebase/firestore');
    const snap = await getDoc(doc(firestore, 'clients', clientId));
    return (snap.data()?.type as string) === 'individual' ? 'individual' : 'company';
  } catch {
    return 'company';
  }
}

export function ActiveCompanyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const staff = isStaff(user?.role);
  const [actingClientId, setActingClientId] = useState<string | null>(() =>
    staff ? readActing() : null,
  );
  const [actingName, setActingName] = useState<string | null>(null);
  const setActingClient = useCallback((id: string | null) => {
    writeActing(id);
    setActingClientId(id);
  }, []);
  useEffect(() => {
    if (!staff || !actingClientId) {
      setActingName(null);
      return;
    }
    let live = true;
    void (async () => {
      const { firestore } = getFirebase();
      const { doc, getDoc } = await import('firebase/firestore');
      const snap = await getDoc(doc(firestore, 'clients', actingClientId)).catch(() => null);
      if (live) setActingName((snap?.data()?.companyName as string | undefined) ?? null);
    })();
    return () => {
      live = false;
    };
  }, [staff, actingClientId]);
  const [company, setCompany] = useState<MyMembership | null>(null);
  const [candidates, setCandidates] = useState<string[]>([]);
  const [personal, setPersonal] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const legacyKey = (user?.permissions ?? []).join(',');

  const load = useCallback(async () => {
    setLoading(true);
    const legacy = legacyKey ? legacyKey.split(',') : [];
    try {
      const res = await listMyClientInvitations();
      const active = res.memberships.filter((m) => m.status === 'active');
      // The legacy link list is a source of truth here as much as the
      // memberships are: most companies are not migrated, so for them it is
      // the ONLY source, and for a half-migrated account it is where the
      // second company hides. The server types those rows too — this
      // context cannot, and typing them is the whole point.
      const typed = new Map<string, ClientKind>();
      for (const m of active) typed.set(m.clientId, m.clientType ?? 'company');
      for (const l of res.legacyLinks ?? []) typed.set(l.clientId, l.clientType);
      /**
       * THE MEMBERSHIP IS AUTHORITATIVE, including when it says no.
       *
       * A company the server holds a membership document for has been
       * answered about — `suspended`, `removed`, `invited` and `prepared`
       * are all answers, and none of them is an active context. The
       * `users.permissions` fallback below used to fire for them anyway,
       * because all it could see was that they were not in `typed`: a
       * suspension somebody imposed, or an invitation nobody had accepted,
       * came back as an active company on the strength of a legacy field
       * that had not been cleaned up. That is the new model losing to the
       * field it replaced.
       *
       * `knownClientIds` is what separates "the answer is no" from "never
       * heard of it". An older server sends none, and then the memberships
       * it did return are the best available answer.
       */
      const decided = new Set<string>([
        ...(res.knownClientIds ?? res.memberships.map((m) => m.clientId)),
        ...(res.legacyLinks ?? []).map((l) => l.clientId),
      ]);
      /**
       * Anything in `permissions` the server has NOT decided about. This is
       * a genuinely pre-model account — for most companies the legacy list
       * is still the only record there is — so it counts, typed
       * conservatively as a company.
       */
      for (const id of legacy) if (!decided.has(id) && !typed.has(id)) typed.set(id, 'company');

      const companyIds = [...typed].filter(([, k]) => k === 'company').map(([id]) => id);
      const personalIds = [...typed].filter(([, k]) => k === 'individual').map(([id]) => id);
      setCandidates(companyIds);
      setPersonal(personalIds);
      // Exactly one, or none. More than one is a conflict, and picking from
      // it would show one company's data under another's name.
      setCompany(
        companyIds.length === 1
          ? (active.find((m) => m.clientId === companyIds[0])
            ?? ({ clientId: companyIds[0] } as MyMembership))
          : null,
      );
    } catch {
      // The callable is refused for accounts the new model knows nothing
      // about; the legacy list still answers for them — but it is a bare
      // list of ids, so the types have to be fetched one by one.
      const kinds = await Promise.all(legacy.map(kindOfClient));
      const companyIds = legacy.filter((_, i) => kinds[i] === 'company');
      setCandidates(companyIds);
      setPersonal(legacy.filter((_, i) => kinds[i] === 'individual'));
      setCompany(companyIds.length === 1 ? ({ clientId: companyIds[0] } as MyMembership) : null);
    } finally {
      // ALWAYS cleared. An account with no company used to leave the portal
      // spinning for ever, because nothing told the screens the answer was
      // "none".
      setLoading(false);
    }
  }, [legacyKey]);

  useEffect(() => {
    if (staff) {
      setLoading(false);
      return;
    }
    void load();
  }, [load, staff]);

  const value = useMemo<ActiveCompany>(
    () =>
      staff
        ? {
            clientId: actingClientId,
            companyName: actingName,
            loading: false,
            conflict: false,
            conflictingClientIds: [],
            personalClientIds: [],
            refresh: async () => {},
            actingAsStaff: true,
            setActingClient,
          }
        : {
            clientId: company?.clientId ?? null,
            companyName: company?.companyName ?? null,
            loading,
            conflict: candidates.length > 1,
            conflictingClientIds: candidates.length > 1 ? candidates : [],
            personalClientIds: personal,
            refresh: load,
            actingAsStaff: false,
            setActingClient,
          },
    [staff, actingClientId, actingName, company, loading, candidates, personal, load, setActingClient],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useActiveCompany(): ActiveCompany {
  return useContext(Ctx);
}

/**
 * The client ids a list screen should query — the ONE employer company, or,
 * for somebody who has none, their own B2C records.
 *
 * Six screens took `user.permissions` instead, which is the legacy link list.
 * That is not choosing the wrong company so much as refusing to choose: on an
 * account linked to two, every list merged both companies' orders, hours and
 * invoices into one view with nothing saying so. An array because the
 * subscriptions are written to fan out, not because there can be two.
 *
 * The personal fallback is what keeps a B2C-only account working: their
 * orders and invoices belong to an `individual` client, which is not a
 * company and must never be scoped to as one — but it is still theirs to
 * see. An account with BOTH gets its employer here, and nothing personal:
 * the B2B lists are the company's, and mixing a private booking into them
 * is the same category error in the other direction.
 */
export function useActiveClientIds(): string[] {
  const { clientId, personalClientIds } = useActiveCompany();
  return useMemo(
    () => (clientId ? [clientId] : personalClientIds),
    [clientId, personalClientIds],
  );
}
